// src/utils/printDownload.js
//
// Install once (project root):  npm i jszip
//
// Usage in components:
//   import { printEntry, downloadEntry, setPrintDownloadTheme } from "../utils/printDownload";
//   printEntry(entry, "proof-of-life");
//   await downloadEntry(entry, "proof-of-life"); // shows progress overlay
//
// entry shape (universal):
//   {
//     title: "Proof of Life",
//     fields: [{ label: "Date", value: "2025-09-03 12:34" }, { label: "Type", value:"video" }, ...],
//     attachments: [
//       { url: "https://.../file.webm", type: "video", filename?: "name.webm" },
//       { url: "https://.../image.jpg", type: "photo" | "image" },
//       { url: "https://.../sound.webm", type: "audio" },
//     ]
//   }

let THEME = {
  brandName: "LastWishBox",
  logoUrl: "/Lastwishbox-logo.png",
  colors: {
    primary: "#f15822", // brand orange
    accent: "#657899",  // slate from your login button
    text: "#111827",
    subtle: "#6b7280",
    cardBg: "#ffffff",
    bg: "#ffffff",
    border: "#e5e7eb",
    progressTrack: "#f3f4f6",
  },
  radius: 12,
};

export function setPrintDownloadTheme(partial = {}) {
  // shallow merge is fine here; pass {logoUrl, brandName, colors:{...}}
  THEME = {
    ...THEME,
    ...partial,
    colors: { ...THEME.colors, ...(partial.colors || {}) },
  };
}

function sanitizeFilename(name = "entry") {
  return String(name)
    .replace(/[^a-z0-9_\-.]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "entry";
}

function extFromContentType(ct = "", fallback = "bin") {
  const t = ct.toLowerCase();
  if (t.startsWith("image/")) return t.split("/")[1] || "img";
  if (t.startsWith("video/")) return t.split("/")[1] || "vid";
  if (t.startsWith("audio/")) return t.split("/")[1] || "aud";
  if (t.includes("pdf")) return "pdf";
  if (t.includes("msword")) return "doc";
  if (t.includes("officedocument.wordprocessingml")) return "docx";
  return fallback;
}

function extFromUrl(url = "", fallback = "bin") {
  try {
    const u = new URL(url);
    const m = (u.pathname || "").match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toLowerCase() : fallback;
  } catch {
    const m = String(url).match(/\.([a-z0-9]+)(\?|#|$)/i);
    return m ? m[1].toLowerCase() : fallback;
  }
}

function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function mediaSrc(att, localPath) {
  // Prefer the local file inside the ZIP if present, otherwise original URL.
  return (att && (att._local || localPath)) || (att && att.url) || "";
}

function mediaTag(att, localPath) {
  const type = (att?.type || "").toLowerCase();
  const src = mediaSrc(att, localPath);
  if (!src) return "";

  if (type === "photo" || type === "image") {
    return `<img src="${src}" alt="" style="max-width:100%;border-radius:${THEME.radius}px" />`;
  }
  if (type === "video") {
    return `<video src="${src}" controls playsinline style="width:100%;max-width:720px;border-radius:${THEME.radius}px"></video>`;
  }
  if (type === "audio") {
    return `<audio src="${src}" controls style="width:100%"></audio>`;
  }
  return `<a href="${src}" target="_blank" rel="noopener">Download file</a>`;
}

function buildHtml(entry, { localMediaBase = null } = {}) {
  const title = escapeHtml(entry?.title || "Entry");
  const fields = Array.isArray(entry?.fields) ? entry.fields : [];
  const attachments = Array.isArray(entry?.attachments) ? entry.attachments : [];

  const fieldRows = fields
    .map(
      (f) => `
        <tr>
          <th>${escapeHtml(f.label || "")}</th>
          <td>${escapeHtml(
            typeof f.value === "string" ? f.value : f.value == null ? "" : String(f.value)
          )}</td>
        </tr>`
    )
    .join("");

  const mediaBlocks = attachments
    .map((a, i) => {
      const local = a?._local || (localMediaBase ? `${localMediaBase}/file-${i + 1}` : null);
      return `
        <div class="media">
          ${mediaTag(a, local)}
          ${
            !local && a?.url
              ? `<div class="hint"><a href="${a.url}" target="_blank" rel="noopener">Open original</a></div>`
              : ""
          }
        </div>`;
    })
    .join("");

  const C = THEME.colors;
  const R = THEME.radius;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title} — ${escapeHtml(THEME.brandName)}</title>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <style>
    :root{
      --c-text:${C.text};
      --c-subtle:${C.subtle};
      --c-border:${C.border};
      --c-bg:${C.bg};
      --c-card:${C.cardBg};
      --c-primary:${C.primary};
      --radius:${R}px;
    }
    *{box-sizing:border-box}
    body{
      font-family:system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif;
      color:var(--c-text); background:var(--c-bg); margin:0; padding:24px; line-height:1.45;
    }
    .wrap{max-width:900px;margin:0 auto}
    .brand{display:flex;align-items:center;gap:12px;margin-bottom:8px}
    .brand img{height:40px;width:auto}
    h1{font-size:22px;margin:0 0 10px}
    .card{border:1px solid var(--c-border);border-radius:var(--radius);padding:16px;background:var(--c-card);margin:10px 0}
    table{width:100%;border-collapse:collapse}
    th{width:180px;text-align:left;color:var(--c-subtle);padding:6px 8px;vertical-align:top}
    td{padding:6px 8px}
    .media{margin-top:12px}
    .hint{font-size:12px;color:var(--c-subtle);margin-top:6px}
    .divider{height:1px;background:var(--c-border);margin:14px 0}
    @media print{
      body{padding:0}
      .hint{display:none}
      a[href]:after{content:" (" attr(href) ")"; font-size:10px; color:var(--c-subtle)}
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="brand">
      ${THEME.logoUrl ? `<img src="${THEME.logoUrl}" alt="${escapeHtml(THEME.brandName)} logo" />` : ""}
      <div style="font-weight:700;color:var(--c-text)">${escapeHtml(THEME.brandName)}</div>
    </div>
    <h1>${title}</h1>
    <div class="card">
      <table>${fieldRows}</table>
    </div>
    ${
      mediaBlocks
        ? `<div class="card"><div style="font-weight:600;margin-bottom:8px">Attachments</div>${mediaBlocks}</div>`
        : ""
    }
  </div>
</body>
</html>`;
}

// ------- Print --------
export function printEntry(entry, filenameBase = "entry") {
  try {
    const html = buildHtml(entry, { localMediaBase: null });
    const win = window.open("", "_blank", "noopener,noreferrer");
    if (!win) {
      // Fallback: download HTML if popup blocked
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sanitizeFilename(filenameBase) + ".html";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 250);
  } catch (e) {
    console.error("printEntry error", e);
    alert("Unable to open print view. Please allow pop-ups and try again.");
  }
}

// ------- Progress overlay (auto) --------
function makeProgressOverlay() {
  const overlay = document.createElement("div");
  overlay.style.cssText =
    "position:fixed;inset:0;background:rgba(17,24,39,.35);display:flex;align-items:center;justify-content:center;z-index:99999";
  const box = document.createElement("div");
  box.style.cssText =
    `background:#fff;border:1px solid ${THEME.colors.border};border-radius:${THEME.radius}px;` +
    "padding:16px 18px;min-width:260px;max-width:80vw;box-shadow:0 10px 40px rgba(0,0,0,.18)";
  const title = document.createElement("div");
  title.style.cssText = `font-weight:700;margin-bottom:6px;color:${THEME.colors.text}`;
  title.textContent = "Preparing download…";
  const barWrap = document.createElement("div");
  barWrap.style.cssText = `height:8px;border-radius:999px;background:${THEME.colors.progressTrack};overflow:hidden`;
  const bar = document.createElement("div");
  bar.style.cssText = `height:8px;width:0%;background:${THEME.colors.primary};transition:width .2s ease`;
  const small = document.createElement("div");
  small.style.cssText = `font-size:12px;color:${THEME.colors.subtle};margin-top:8px`;
  small.textContent = "";

  barWrap.appendChild(bar);
  box.appendChild(title);
  box.appendChild(barWrap);
  box.appendChild(small);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  return {
    update(msg, pct) {
      if (title) title.textContent = msg || "Preparing download…";
      if (typeof pct === "number" && isFinite(pct)) {
        bar.style.width = Math.max(0, Math.min(100, pct)) + "%";
      }
      if (small) small.textContent = pct != null ? `${Math.round(pct)}%` : "";
    },
    remove() {
      try { overlay.remove(); } catch {}
    },
  };
}

// ------- Helpers --------
async function downloadBlobAs(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function fetchAsBlob(url) {
  const r = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!r.ok) throw new Error(`Fetch failed (${r.status}) for ${url}`);
  const ct = r.headers.get("content-type") || "";
  const blob = await r.blob();
  return { blob, contentType: ct };
}

// ------- ZIP Download (with progress) --------
export async function downloadEntry(entry, filenameBase = "entry", opts = {}) {
  const { onProgress } = opts || {};
  const overlay = makeProgressOverlay();

  const base = sanitizeFilename(filenameBase);

  // Lazy import JSZip
  let JSZip;
  try {
    const mod = await import("jszip");
    JSZip = mod && (mod.default || mod);
  } catch (e) {
    console.error("JSZip not available. Falling back to HTML-only download.", e);
    const html = buildHtml(entry, { localMediaBase: null });
    const blob = new Blob([html], { type: "text/html" });
    await downloadBlobAs(`${base}.html`, blob);
    return;
  }

  const zip = new JSZip();
  const mediaFolder = zip.folder("media");
  const attachments = Array.isArray(entry?.attachments) ? entry.attachments : [];
  const localRefs = [];

  const total = attachments.length || 1;
  let step = 0;

  const stepUpdate = (label) => {
    const pct = Math.min(99, Math.round((step / (total + 1)) * 100)); // reserve 1 step for zipping
    overlay.update(label, pct);
    if (typeof onProgress === "function") onProgress(pct, label);
  };

  try {
    // Download attachments
    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];
      stepUpdate(`Downloading attachment ${i + 1} of ${attachments.length}…`);
      if (!att?.url) {
        localRefs.push(null);
        step++;
        continue;
      }

      try {
        const { blob, contentType } = await fetchAsBlob(att.url);
        const ext =
          (att.filename && att.filename.split(".").pop()) ||
          extFromUrl(att.url, extFromContentType(contentType, "bin"));
        const safeName =
          sanitizeFilename(att.filename || `file-${i + 1}`) + "." + ext;
        mediaFolder.file(safeName, blob, { binary: true });
        localRefs.push(`media/${safeName}`);
      } catch (e) {
        console.warn("Attachment fetch failed, skipping:", att.url, e);
        localRefs.push(null);
      }
      step++;
    }

    // HTML (prefer local paths when available)
    const entryWithLocals = {
      ...entry,
      attachments: attachments.map((a, i) =>
        localRefs[i] ? { ...a, _local: localRefs[i] } : a
      ),
    };
    const html = buildHtml(entryWithLocals, { localMediaBase: "media" });
    zip.file("entry.html", html);

    // Build ZIP with DEFLATE compression and show progress
    overlay.update("Zipping files…", 99);
    if (typeof onProgress === "function") onProgress(99, "Zipping files…");

    const zipBlob = await zip.generateAsync(
      { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
      (meta) => {
        const pct = Math.min(100, Math.round(99 + meta.percent / 100));
        overlay.update(`Zipping files…`, pct);
        if (typeof onProgress === "function") onProgress(pct, "Zipping files…");
      }
    );

    overlay.update("Downloading…", 100);
    if (typeof onProgress === "function") onProgress(100, "Downloading…");
    await downloadBlobAs(`${base}.zip`, zipBlob);
  } finally {
    overlay.remove();
  }
}
